/**
 * DM-13 round 3 — `/docs` landing hub (per UX feedback «всегда кидает на аудитора»).
 *
 * Раньше — auto-redirect на `/docs/{userRole}` → пользователь не видел документации
 * других ролей (cross-role browse невозможен). Теперь — landing с «Ваша роль» +
 * 6 других ролей + сквозные разделы.
 */

import * as m from '@staiter/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { DocsLayout } from '~/components/docs';
import { useAuthStore } from '~/store/auth';
import styles from './_auth.docs.index.module.css';

export const Route = createFileRoute('/_auth/docs/')({
  component: DocsLanding,
});

interface RoleEntry {
  slug: string;
  title: string;
  desc: string;
}

const ROLES: RoleEntry[] = [
  {
    slug: 'manager',
    title: 'Руководитель',
    desc: 'Утренний обзор, согласования уровней 2 и 3, контроль показателей, закрытие периода.',
  },
  {
    slug: 'accountant',
    title: 'Бухгалтер',
    desc: 'Конфликты, движения по складу, инвентаризация, справочники, работа с агентом.',
  },
  {
    slug: 'reviewer',
    title: 'Старший бухгалтер',
    desc: 'Очередь согласований уровней 2 и 3, второй подписант, журнал действий.',
  },
  {
    slug: 'admin',
    title: 'Администратор',
    desc: 'Подключения, доступы, обслуживание агента и фоновых задач.',
  },
  {
    slug: 'viewer',
    title: 'Аудитор',
    desc: 'Доказательная база, журнал действий, квартальный аудит, контроль целостности.',
  },
  {
    slug: 'warehouse-operator',
    title: 'Кладовщик',
    desc: 'Мастер ввода движений, инвентаризация, партии, частые ситуации.',
  },
  {
    slug: 'shared',
    title: 'Общие материалы',
    desc: 'Вход, навигация, темы оформления, горячие клавиши.',
  },
];

function mapUserRoleToDocsRole(userRole: string): string {
  const map: Record<string, string> = {
    admin: 'admin',
    manager: 'manager',
    accountant: 'accountant',
    reviewer: 'reviewer',
    operator: 'warehouse-operator',
    warehouse: 'warehouse-operator',
    warehouse_operator: 'warehouse-operator',
    viewer: 'viewer',
  };
  return map[userRole] ?? 'viewer';
}

function DocsLanding(): JSX.Element {
  const userRole = useAuthStore((s) => s.user?.role ?? 'viewer');
  const myRoleSlug = mapUserRoleToDocsRole(userRole);
  const myRole = ROLES.find((r) => r.slug === myRoleSlug);
  const otherRoles = ROLES.filter((r) => r.slug !== myRoleSlug);

  return (
    <DocsLayout
      toc={undefined}
      currentRole=""
      currentSlug={null}
      hideSidebar
      breadcrumbs={[{ label: 'Документация' }]}
    >
      <div className={styles.page}>
        <header className={styles.heroHeader}>
          <h1 className={styles.title}>{m.ui_dokumentaciya_staiter()}</h1>
          <p className={styles.lede}>
            Выберите свою роль для рабочих сценариев или посмотрите, что увидят коллеги в других
            ролях.
          </p>
        </header>

        {myRole ? (
          <>
            <div className={styles.sectionLabel}>{m.ui_vasha_rol()}</div>
            <a className={styles.myRoleCard} href={`/docs/${myRole.slug}`}>
              <span className={styles.myRoleKicker}>Вы вошли как {userRole}</span>
              <div className={styles.myRoleRow}>
                <h2 className={styles.myRoleTitle}>{myRole.title}</h2>
                <span className={styles.myRoleArrow}>{m.ui_otkryt()}</span>
              </div>
              <p className={styles.myRoleDesc}>{myRole.desc}</p>
            </a>
          </>
        ) : null}

        <div className={styles.sectionLabel}>{m.ui_drugie_roli()}</div>
        <div className={styles.roleGrid}>
          {otherRoles.map((r) => (
            <a key={r.slug} className={styles.roleCard} href={`/docs/${r.slug}`}>
              <h3 className={styles.roleCardTitle}>{r.title}</h3>
              <p className={styles.roleCardDesc}>{r.desc}</p>
            </a>
          ))}
        </div>

        <div className={styles.sectionLabel}>{m.ui_skvoznye_razdely()}</div>
        <div className={styles.crossSection}>
          <a className={styles.crossLink} href="/docs/shared/login-2fa">
            {m.ui_vhod_i_2fa()}
          </a>
          <a className={styles.crossLink} href="/docs/shared/navigation">
            {m.ui_navigaciya()}
          </a>
          <a className={styles.crossLink} href="/docs/shared/themes-i18n">
            {m.ui_temy_oformleniya()}
          </a>
          <a className={styles.crossLink} href="/docs/shared/keyboard-shortcuts">
            {m.ui_goryachie_klavishi()}
          </a>
          <a className={styles.crossLink} href="/docs/search">
            {m.ui_poisk_po_dokumentacii()}
          </a>
        </div>
      </div>
    </DocsLayout>
  );
}
